import { Card, FormGroup, NonIdealState, ProgressBar } from "@blueprintjs/core";
import { MediaImage } from "@package/ui-apollo-components";
import {
  type GetServiceQuery,
  type Scalars,
  type ServiceOptionAppCmsFunction,
  useGetServiceQuery,
} from "@package/ui-gql/index-data-access";
import { type WritableDraft } from "immer";
import { useEffect, useState } from "react";
import { EDIT_CARD_WIDTH_CLASS } from "../../constants/contants";
import { MediaTagSelector } from "../MediaTagSelector/MediaTagSelector";
import { ServiceDataFormField } from "./ServiceDataFormField";

type ServiceDataFormProps = {
  id?: string | null;
  appCmsType: "post" | "service";
  card?: boolean;
  data?: Scalars["JSONObject"]["output"];
  mediaId?: string | null;
  onMediaIdChanged?: (mediaId: string | null) => void;
  onSourceChanged: (
    callback: (draft: WritableDraft<Scalars["JSONObject"]["output"]>) => void,
  ) => void;
  showTitle?: boolean;
};

export function ServiceDataForm({
  id,
  appCmsType,
  card = true,
  data,
  mediaId,
  onMediaIdChanged,
  onSourceChanged,
  showTitle = true,
}: ServiceDataFormProps) {
  const [options, setOptions] = useState<ServiceOptionAppCmsFunction>();

  const {
    data: dataService,
    loading: loadingService,
    error: errorService,
  } = useGetServiceQuery({
    variables: {
      id: id ?? "",
    },
    skip: !id,
  });

  useEffect(
    function effectServiceDataChanged() {
      setOptions(
        (dataService?.item?.options?.cms?.[
          appCmsType
        ] as ServiceOptionAppCmsFunction) ?? undefined,
      );
    },
    [appCmsType, dataService?.item?.options],
  );

  if (errorService) {
    return (
      <NonIdealState
        description="Failed loading service form data"
        icon="warning-sign"
        title="Error"
      />
    );
  }

  if (loadingService) {
    return <ProgressBar animate intent="primary" stripes />;
  }

  if (options?.showMediaInput || options?.extraFields?.length) {
    if (card) {
      return (
        <Card className="mt-4">
          <div className={`${EDIT_CARD_WIDTH_CLASS}`}>
            <SessionDataFormContents
              onSourceChanged={onSourceChanged}
              options={options}
              data={data}
              dataService={dataService}
              mediaId={mediaId}
              onMediaIdChanged={onMediaIdChanged}
              showTitle={showTitle}
            />
          </div>
        </Card>
      );
    }

    return (
      <SessionDataFormContents
        onSourceChanged={onSourceChanged}
        options={options}
        data={data}
        dataService={dataService}
        mediaId={mediaId}
        onMediaIdChanged={onMediaIdChanged}
        showTitle={showTitle}
      />
    );
  }

  return null;
}

function SessionDataFormContents({
  data,
  dataService,
  mediaId,
  onMediaIdChanged,
  onSourceChanged,
  options,
  showTitle = true,
}: {
  data?: Scalars["JSONObject"]["output"];
  dataService?: GetServiceQuery;
  mediaId?: string | null;
  onMediaIdChanged?: (mediaId: string | null) => void;
  onSourceChanged: (
    callback: (draft: WritableDraft<Scalars["JSONObject"]["output"]>) => void,
  ) => void;
  options: ServiceOptionAppCmsFunction;
  showTitle: boolean;
}) {
  return (
    <>
      {showTitle && (
        <h6 className="bp6-heading mb-4">
          {dataService?.item?.name ?? "No Service"}
        </h6>
      )}
      {!!options.showMediaInput && (
        <>
          <FormGroup label="Media File" labelFor="control-media">
            <div className="mb-2">
              <MediaTagSelector
                fill
                large
                minimal
                id={mediaId ?? undefined}
                onRemoveMedia={() => onMediaIdChanged?.(null)}
                onSelectMedia={(mediaId) => onMediaIdChanged?.(mediaId ?? null)}
              />
            </div>
            <MediaImage id={mediaId ?? undefined} />
          </FormGroup>
        </>
      )}
      {options.extraFields?.map((field, index) => (
        <ServiceDataFormField
          key={index}
          field={field}
          data={data}
          onSourceChanged={onSourceChanged}
        />
      ))}
    </>
  );
}
